import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GlassCard } from "@/components/GlassCard";
import { Reveal, SectionKicker } from "@/components/Reveal";
import SEO from "@/components/SEO";
import { container, goldBtn, SECTORS } from "@/lib/constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600711269860-ff440f53ed42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxldXJvcGUlMjBza3lsaW5lJTIwbmlnaHQlMjBtb25vY2hyb21lfGVufDB8fHxibGFja3wxNzg3NTMxOTg1fDA&ixlib=rb-4.1.0&q=85";

const SECTOR_BLURB = {
  real_estate: "Commercial, residential, and mixed-use across primary European markets.",
  private_credit: "Direct lending and structured credit with robust security packages.",
  real_assets: "Infrastructure, the energy transition, and agriculture.",
  late_stage_equity: "Pre-IPO and growth rounds in category-leading businesses.",
};

const PROCESS = [
  ["01", "Application & verification", "We verify principal status and mandate before any access is granted."],
  ["02", "Mandate submission", "You define sectors, geographies, and ticket size. We align the pipeline."],
  ["03", "Deal matching & introduction", "Off-market opportunities are matched discreetly to your mandate."],
  ["04", "Execution support", "We coordinate introductions and support diligence through to close."],
];

export default function Home() {
  return (
    <PublicLayout>
      <SEO
        title="Access Europe's most discreet deal flow"
        description="Prime Deal Gateway is a by-invitation acquisition desk for verified principals. Discreet access to off-market European deals across real estate, private credit, real assets, and late-stage equity."
        keywords="off-market deals, family office, private credit, private equity secondary, European real estate, institutional deal flow"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background:
            "radial-gradient(1200px 600px at 18% 0%, hsl(220 10% 13% / 0.6), transparent 60%), radial-gradient(900px 500px at 82% 8%, hsl(40 45% 55% / 0.10), transparent 55%)" }} />
        <div className={`relative ${container} py-20 lg:py-28 grid gap-12 lg:grid-cols-12 items-center`}>
          <div className="lg:col-span-7">
            <SectionKicker>By invitation &middot; Off-market European deal flow</SectionKicker>
            <h1 className="pdg-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-[-0.02em] text-foreground">
              Access Europe&rsquo;s most discreet deal flow.
            </h1>
            <p className="mt-6 text-lg text-foreground/80 max-w-xl leading-8">
              By invitation. For verified principals only.
            </p>
            <div className="mt-9">
              <Link to="/auth?tab=apply" data-testid="hero-apply-for-access-button" className={goldBtn}>
                Apply for Access <ArrowRight size={16} />
              </Link>
            </div>
            <p className="mt-8 text-xs text-muted-foreground tracking-wide max-w-md leading-6">
              Family offices &middot; private investment vehicles &middot; operators. Verification required before any deal is shown.
            </p>
          </div>
          <div className="hidden lg:block lg:col-span-5">
            <GlassCard className="p-2">
              <div className="overflow-hidden rounded-xl">
                <img src={HERO_IMG} alt="European skyline at night"
                  className="w-full h-[420px] object-cover grayscale contrast-110" />
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className={`${container} py-16 lg:py-20`}>
        <Reveal><SectionKicker>Where we operate</SectionKicker>
          <h2 className="pdg-serif text-3xl sm:text-4xl text-foreground max-w-2xl leading-tight">
            Four sectors. One discreet standard of access.
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {Object.entries(SECTORS).map(([k, v], i) => (
            <Reveal key={k} delay={i * 0.05}>
              <GlassCard hover className="p-7 h-full">
                <h3 className="pdg-serif text-xl text-foreground">{v}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-7">{SECTOR_BLURB[k]}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/sectors" className="text-sm text-[hsl(var(--pdg-gold))] pdg-link-underline">
            Explore the sectors
          </Link>
        </div>
      </section>

      {/* Process */}
      <section className="border-y border-border bg-[hsl(var(--pdg-charcoal))]">
        <div className={`${container} py-16 lg:py-20`}>
          <Reveal><SectionKicker>The process</SectionKicker>
            <h2 className="pdg-serif text-3xl sm:text-4xl text-foreground">Considered, from first contact to close.</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            {PROCESS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.05}>
                <div className="rounded-2xl p-6 border border-border bg-[hsl(var(--pdg-elevated)/0.4)] h-full">
                  <div className="pdg-mono text-xs text-[hsl(var(--pdg-gold))]">{n}</div>
                  <h3 className="mt-4 pdg-serif text-lg text-foreground">{t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-7">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className={`${container} py-20 lg:py-28`}>
        <Reveal>
          <GlassCard className="p-10 lg:p-16 text-left">
            <SectionKicker>Membership</SectionKicker>
            <h2 className="pdg-serif text-3xl sm:text-4xl text-foreground max-w-2xl leading-tight">
              Access is extended by invitation, following verification.
            </h2>
            <p className="mt-5 text-base text-foreground/75 max-w-xl leading-8">
              We do not publish pricing. Speak to the desk to understand the membership tier
              appropriate to your mandate.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/auth?tab=apply" className={goldBtn}>Apply for Access <ArrowRight size={16} /></Link>
              <Link to="/membership" className="inline-flex items-center h-11 px-5 text-sm text-foreground/80 hover:text-foreground">
                View membership tiers
              </Link>
            </div>
          </GlassCard>
        </Reveal>
      </section>
    </PublicLayout>
  );
}
